import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, Trash2, ArrowRight, Mail, Layers, Megaphone, ShoppingBag, FileText, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import QuoteBuilder, { computeQuote } from "../../components/os/QuoteBuilder";
import QuoteInvoiceModal from "../../components/os/QuoteInvoiceModal";
import QuoteComments from "../../components/os/QuoteComments";
import QuoteHeaderFields from "../../components/os/QuoteHeaderFields";
import TrashDialog, { TrashButton } from "../../components/os/TrashDialog";
import { Download, Send, Link2, ReceiptText, Image as ImageIcon, Check, RefreshCw, Hash } from "lucide-react";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const STAGES = ["new", "qualified", "proposal", "negotiation", "won", "lost"];
const SEGMENTS = ["Hospitality", "Retail", "Residencial", "Corporativo", "Real Estate", "Publicidad", "Marketing", "Evento", "Comunicación", "Branding", "Redes Sociales", "Producción Audiovisual"];
const STAGE_CLS = {
  new: "bg-navy/10 text-navy/60", qualified: "bg-blue-500/10 text-blue-600",
  proposal: "bg-violet/10 text-violet", negotiation: "bg-gold/15 text-gold",
  won: "bg-emerald-500/10 text-emerald-600", lost: "bg-destructive/10 text-destructive",
};
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ScoreBar({ score }) {
  const color = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-gold" : "bg-navy/30";
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 rounded-full bg-navy/5 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${score}%` }} /></div>
      <span className="text-xs text-navy/50 w-6">{score}</span>
    </div>
  );
}

function QuotesRegistry({ quotes, company, setCompany, leads, onOpenLead, tf }) {
  const companies = [...new Set(quotes.map((q) => q.client).filter(Boolean))].sort();
  const filtered = quotes.filter((q) => !company || q.client === company);
  const total = filtered.reduce((s, q) => s + Number(q.amount || 0), 0);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState({ folio_prefix: "", folio_next: 1, folio_pad: 3 });
  useEffect(() => { api.get("/studio/quote-settings").then((r) => setCfg({ folio_prefix: r.data.folio_prefix ?? "", folio_next: r.data.folio_next ?? 1, folio_pad: r.data.folio_pad ?? 3 })).catch(() => {}); }, []);
  const saveCfg = async () => {
    try { await api.put("/studio/quote-settings", { folio_prefix: cfg.folio_prefix, folio_next: Number(cfg.folio_next) || 1, folio_pad: Number(cfg.folio_pad) || 0 }); toast.success("Configuración de folio guardada"); setCfgOpen(false); }
    catch { toast.error("No se pudo guardar"); }
  };
  const preview = `${cfg.folio_prefix || ""}${String(Number(cfg.folio_next) || 1).padStart(Number(cfg.folio_pad) || 0, "0")}`;
  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(String(d).length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return d; }
  };
  const openQuote = (q) => {
    if (q.source === "lead") { const l = leads.find((x) => x.id === q.ref_id); if (l) onOpenLead(l); }
  };
  return (
    <div data-testid="crm-quotes-panel" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-navy/50" data-testid="crm-quotes-count">{filtered.length} presupuesto(s) · {money(total)}</span>
        <button onClick={() => setCfgOpen((o) => !o)} data-testid="crm-folio-config-toggle" className="text-sm text-violet hover:underline inline-flex items-center gap-1"><Hash className="w-4 h-4" /> Configurar folio</button>
        <select value={company} onChange={(e) => setCompany(e.target.value)} data-testid="crm-quotes-company"
          className="ml-auto bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
          <option value="">Todas las empresas</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {cfgOpen && (
        <div className="bg-white border border-navy/10 rounded-lg p-5 space-y-3" data-testid="crm-folio-config">
          <div className="text-sm font-medium text-navy">Folio automático de presupuestos</div>
          <p className="text-xs text-navy/50">Se aplica a todos los presupuestos (CRM, GOÛT y TAGMA). También puedes escribir un folio manual en cada presupuesto.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-navy/50 block mb-1">Prefijo (letras, números, signos)</label><input value={cfg.folio_prefix} onChange={(e) => setCfg({ ...cfg, folio_prefix: e.target.value })} placeholder="Ej. PRES-2026-" className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet" data-testid="crm-folio-prefix" /></div>
            <div><label className="text-xs text-navy/50 block mb-1">Siguiente número</label><input type="number" value={cfg.folio_next} onChange={(e) => setCfg({ ...cfg, folio_next: e.target.value })} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet" data-testid="crm-folio-next" /></div>
            <div><label className="text-xs text-navy/50 block mb-1">Dígitos (relleno con ceros)</label><input type="number" value={cfg.folio_pad} onChange={(e) => setCfg({ ...cfg, folio_pad: e.target.value })} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet" data-testid="crm-folio-pad" /></div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-navy/60">Siguiente folio: <b className="text-violet font-mono" data-testid="crm-folio-preview">{preview}</b></span>
            <button onClick={saveCfg} data-testid="crm-folio-save" className="ml-auto rounded-full bg-navy text-white px-5 py-2 text-sm hover:opacity-90">Guardar configuración</button>
          </div>
        </div>
      )}
      <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto">
        <div className="hidden sm:grid grid-cols-[130px_1.2fr_1.4fr_130px_120px_130px_70px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
          <span>Folio</span><span>Empresa / Cliente</span><span>Concepto</span><span>Monto</span><span>Fecha</span><span>Estado</span><span />
        </div>
        {filtered.length === 0 && <div className="px-5 py-10 text-center text-navy/40">Aún no hay presupuestos. Crea uno desde un Lead o desde un proyecto de Studio.</div>}
        {filtered.map((q) => (
          <div key={`${q.source}-${q.ref_id}`} data-testid={`crm-quote-${q.ref_id}`} className="grid grid-cols-2 sm:grid-cols-[130px_1.2fr_1.4fr_130px_120px_130px_70px] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0">
            <div className="font-mono text-xs text-navy/70">{q.folio || "—"}</div>
            <div className="text-sm font-medium truncate">{q.client}</div>
            <div className="text-sm text-navy/60 truncate">{q.title}</div>
            <div className="text-sm">{money(q.amount)}</div>
            <div className="text-xs text-navy/50">{fmtDate(q.date)}</div>
            <div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${q.source === "lead" ? (STAGE_CLS[q.status] || "bg-navy/10 text-navy/60") : "bg-violet/10 text-violet"}`}>{q.status_label}</span>
            </div>
            <div className="justify-self-end">
              {q.source === "lead" ? (
                <button data-testid={`crm-quote-view-${q.ref_id}`} onClick={() => openQuote(q)} className="text-xs text-violet hover:underline inline-flex items-center gap-1">Ver <ArrowRight className="w-3 h-3" /></button>
              ) : (
                <Link data-testid={`crm-quote-view-${q.ref_id}`} to={`/os/${q.studio}/${q.ref_id}`} className="text-xs text-violet hover:underline inline-flex items-center gap-1">Ver <ArrowRight className="w-3 h-3" /></Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Crm() {
  const { t, lang } = useI18n();
  const tf = useTeamFilter();
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [segments, setSegments] = useState([]);
  const [clients, setClients] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [modal, setModal] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quoteCompany, setQuoteCompany] = useState("");

  const load = useCallback(() => {
    api.get("/crm/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/crm/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/crm/segments").then((r) => setSegments(r.data)).catch(() => {});
    api.get("/crm/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/crm/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveLead = async (lid, stage) => {
    setLeads((ls) => ls.map((l) => (l.id === lid ? { ...l, stage } : l)));
    try { await api.patch(`/crm/leads/${lid}`, { stage }); load(); } catch (e) { toast.error("Error"); load(); }
  };
  const delLead = async (lid) => { await api.delete(`/crm/leads/${lid}`); load(); };
  const [share, setShare] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);

  const [form, setForm] = useState({ name: "", company: "", email: "", segment: "Residencial", value: "", source: "Directo" });
  const [err, setErr] = useState("");
  const create = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/crm/leads", { ...form, value: Number(form.value) || 0 }); toast.success("OK"); setModal(false); setForm({ name: "", company: "", email: "", segment: "Residencial", value: "", source: "Directo" }); load(); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); }
  };

  const TABS = ["pipeline", "leads", "quotes", "clients", "segments"];
  const tabLabel = (tk) => tk === "clients" ? (lang === "en" ? "Clients" : "Clientes") : tk === "quotes" ? "Presupuestos" : t(`os.crm.tabs.${tk}`);

  return (
    <OSLayout title={t("os.nav.crm")}>
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">OMNIA · CRM</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Relationships</h2>
              <p className="mt-2 text-navy/50">{t("os.crm.subtitle")}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <OwnerFilter tf={tf} />
              <TrashButton testid="crm-trash-btn" onClick={() => setTrashOpen(true)} />
              <button data-testid="crm-new-lead" onClick={() => setModal(true)}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {t("os.crm.newLead")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`crm-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          <div className="flex gap-2 border-b border-navy/10 overflow-x-auto">
            {TABS.map((tk) => (
              <button key={tk} data-testid={`crm-tab-${tk}`} onClick={() => setTab(tk)}
                className={`px-4 py-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === tk ? "border-violet text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
                {tabLabel(tk)}{tk === "clients" && clients ? ` (${clients.total})` : ""}
              </button>
            ))}
          </div>

          {/* Pipeline Kanban */}
          {tab === "pipeline" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="crm-pipeline-panel">
              {STAGES.map((st) => {
                const cards = tf.filter(leads).filter((l) => l.stage === st);
                const total = cards.reduce((s, l) => s + Number(l.value || 0), 0);
                return (
                  <div key={st} data-testid={`crm-stage-${st}`}
                    onDragOver={(e) => e.preventDefault()} onDrop={() => dragId && moveLead(dragId, st)}
                    className="bg-white/60 border border-navy/10 rounded-lg p-3 min-h-[120px]">
                    <div className="px-1 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="overline text-navy/50">{t(`os.crm.stages.${st}`)}</span>
                        <span className="text-xs text-navy/30">{cards.length}</span>
                      </div>
                      <div className="text-[11px] text-navy/40 mt-0.5">{money(total)}</div>
                    </div>
                    <div className="space-y-2">
                      {cards.map((l) => (
                        <motion.div key={l.id} layout draggable
                          onDragStart={() => setDragId(l.id)} onDragEnd={() => setDragId(null)}
                          onClick={() => setDrawer(l)}
                          data-testid={`crm-lead-${l.id}`}
                          className="group bg-white border border-navy/10 rounded-lg p-3 cursor-pointer hover:border-violet/40 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium leading-snug">{l.name}</p>
                            <button data-testid={`crm-lead-del-${l.id}`} onClick={(e) => { e.stopPropagation(); delLead(l.id); }}
                              className="opacity-0 group-hover:opacity-100 text-navy/30 hover:text-destructive transition-opacity shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="text-xs text-navy/40 mt-0.5">{l.company}</div>
                          <div className="text-xs text-navy/50 mt-1 flex flex-wrap items-center gap-1.5">
                            <span className="truncate">{money(l.value)}</span>
                            {l.quote?.folio && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet/10 text-violet inline-flex items-center gap-1 shrink-0"><FileText className="w-2.5 h-2.5" /> Presupuesto</span>}
                            {l.converted_project && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">Proyecto</span>}
                          </div>
                          <div className="mt-2"><ScoreBar score={l.score} /></div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Leads table */}
          {tab === "leads" && (
            <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="crm-leads-panel">
              <div className="hidden sm:grid grid-cols-[1.3fr_1fr_1fr_120px_110px_120px_40px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
                <span>{t("os.crm.cols.name")}</span><span>{t("os.crm.cols.company")}</span><span>{t("os.crm.cols.segment")}</span>
                <span>{t("os.crm.cols.value")}</span><span>{t("os.crm.cols.score")}</span><span>{t("os.crm.cols.stage")}</span><span />
              </div>
              {tf.filter(leads).map((l) => (
                <div key={l.id} data-testid={`crm-row-${l.id}`} className="grid grid-cols-2 sm:grid-cols-[1.3fr_1fr_1fr_120px_110px_120px_40px] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0">
                  <div className="font-medium text-sm">{l.name}<div className="text-xs text-navy/40 sm:hidden">{l.company}</div></div>
                  <div className="text-sm text-navy/60 hidden sm:block">{l.company}</div>
                  <div className="text-xs text-navy/50">{l.segment}</div>
                  <div className="text-sm">{money(l.value)}</div>
                  <ScoreBar score={l.score} />
                  <select data-testid={`crm-stage-select-${l.id}`} value={l.stage} onChange={(e) => moveLead(l.id, e.target.value)}
                    className={`text-[11px] px-2 py-1 rounded-full outline-none ${STAGE_CLS[l.stage]}`}>
                    {STAGES.map((s) => <option key={s} value={s}>{t(`os.crm.stages.${s}`)}</option>)}
                  </select>
                  <div className="justify-self-end">
                    <ItemMenu testId={l.id} onInvite={() => setShare({ id: l.id, name: l.name })} onDelete={() => delLead(l.id)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Clientes de studios */}
          {tab === "clients" && (
            <div className="space-y-3" data-testid="crm-clients-panel">
              <p className="text-sm text-navy/50">Clientes con actividad real en GOÛT, TAGMA, VETTA y AURA. {clients ? `${clients.active} activos de ${clients.total}.` : ""}</p>
              {!clients || clients.clients.length === 0 ? (
                <div className="text-navy/40 py-10 text-center border border-dashed border-navy/15 rounded-lg">Aún no hay clientes con proyectos o pedidos en los studios.</div>
              ) : (
                <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
                  {clients.clients.map((c, i) => (
                    <button key={i} data-testid={`crm-client-${i}`} onClick={() => setClientDetail(c)} className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-navy/[0.02] transition-colors">
                      <div className="w-9 h-9 rounded-full bg-gradient-violet flex items-center justify-center text-white text-[11px] shrink-0">{c.client.slice(0, 2).toUpperCase()}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {c.client}
                          {c.active
                            ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Activo</span>
                            : <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/5 text-navy/40">Inactivo</span>}
                        </div>
                        {c.email && <div className="text-xs text-navy/40">{c.email}</div>}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {c.items.map((it, j) => (
                            <span key={j} className="text-[11px] px-2 py-0.5 rounded-full bg-navy/5 text-navy/55"><span className="text-violet font-medium">{it.studio}</span> · {it.name}</span>
                          ))}
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <div className="flex flex-wrap gap-1 justify-end max-w-[120px]">
                          {c.studios.map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-violet/10 text-violet">{s}</span>)}
                        </div>
                        <ArrowRight className="w-4 h-4 text-navy/30" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Segments */}
          {tab === "segments" && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="crm-segments-panel">
              {segments.map((s) => (
                <div key={s.segment} data-testid={`crm-segment-${s.segment}`} className="bg-white border border-navy/10 rounded-lg p-6">
                  <h3 className="text-lg font-medium">{s.segment}</h3>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div><div className="overline text-navy/40 text-[0.55rem]">{t("os.crm.segment.count")}</div><div className="text-xl font-medium mt-1">{s.count}</div></div>
                    <div><div className="overline text-navy/40 text-[0.55rem]">{t("os.crm.segment.value")}</div><div className="text-xl font-medium mt-1">{money(s.value)}</div></div>
                    <div><div className="overline text-navy/40 text-[0.55rem]">{t("os.crm.segment.avgScore")}</div><div className="text-xl font-medium mt-1 text-violet">{s.avg_score}</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Presupuestos (registro por empresa) */}
          {tab === "quotes" && (
            <QuotesRegistry quotes={quotes} company={quoteCompany} setCompany={setQuoteCompany} leads={leads} onOpenLead={setDrawer} tf={tf} />
          )}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setModal(false)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="crm-lead-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">{t("os.crm.create.title")}</h3>
              <button onClick={() => setModal(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required placeholder={t("os.crm.create.name")} data-testid="crm-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input placeholder={t("os.crm.create.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
              <input type="email" placeholder={t("os.crm.create.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="crm-input-segment" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} className={inputCls}>
                  {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" placeholder={t("os.crm.create.value")} data-testid="crm-input-value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} />
              </div>
              {err && <p className="text-sm text-destructive">{err}</p>}
              <button data-testid="crm-lead-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.crm.create.submit")}</button>
            </form>
          </motion.div>
        </div>
      )}
      {share && <ShareDialog collection="crm_leads" id={share.id} title={share.name} onClose={() => setShare(null)} />}
      {trashOpen && <TrashDialog onClose={() => setTrashOpen(false)} onChanged={load} testid="crm-trash-dialog" />}
      {clientDetail && <ClientDetail client={clientDetail} onClose={() => setClientDetail(null)} />}
      {drawer && <LeadDrawer lead={drawer} onClose={() => setDrawer(null)} onSaved={load} />}
    </OSLayout>
  );
}

const STUDIO_ICON = { "GOÛT": Layers, TAGMA: Megaphone, AURA: ShoppingBag, VETTA: ShoppingBag };

function LeadDrawer({ lead, onClose, onSaved }) {
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [email, setEmail] = useState(lead.email || "");
  const [approvalStudio, setApprovalStudio] = useState("gout");
  const [approvalUrl, setApprovalUrl] = useState("");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lockReason, setLockReason] = useState("");
  const [folio, setFolio] = useState("");
  const [currentFolio, setCurrentFolio] = useState("");
  const [templateTerms, setTemplateTerms] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [accent, setAccent] = useState("#7c5cff");
  const logoRef = useRef();
  const converted = lead.converted_project;
  const input = "bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet transition-colors text-sm w-full";
  const setT = (k, val) => setQuote((q) => ({ ...q, terms: { ...(q.terms || {}), [k]: val } }));

  useEffect(() => {
    api.get(`/crm/leads/${lead.id}/quote`).then((r) => {
      const q = r.data || {};
      setQuote({ items: q.items || [], margin_pct: q.margin_pct || 0, tax_pct: q.tax_pct ?? 16, margin_included: !!q.margin_included, tax_included: !!q.tax_included, hide_total: !!q.hide_total, terms: q.terms || {} });
      setLocked(!!q.locked); setLockReason(q.locked_reason || ""); setCurrentFolio(q.folio || "");
    }).catch(() => setQuote({ items: [], margin_pct: 0, tax_pct: 16, margin_included: false, tax_included: false, terms: {} }));
  }, [lead.id]);

  // Plantilla de condiciones + logo (compartidos por tenant con Studio)
  useEffect(() => {
    api.get(`/studio/quote-terms-template`).then((r) => { setTemplateTerms(r.data?.terms || {}); if (r.data?.accent_color) setAccent(r.data.accent_color); }).catch(() => {});
    loadLogo();
    return () => { setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); };
  }, []);
  const saveAccent = async (color) => {
    setAccent(color);
    try { await api.put(`/studio/quote-terms-template`, { accent_color: color }); }
    catch { /* silencioso */ }
  };
  const loadLogo = async () => {
    try { const res = await api.get(`/studio/quote-logo`, { responseType: "blob" }); setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); }); }
    catch { setLogoUrl(null); }
  };
  const uploadLogo = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setLogoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/studio/quote-logo`, fd); toast.success("Logo actualizado · aparecerá en el PDF"); await loadLogo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el logo"); }
    finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ""; }
  };
  const removeLogo = async () => {
    try { await api.delete(`/studio/quote-logo`); toast.success("Logo eliminado"); setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); }
    catch { toast.error("No se pudo eliminar"); }
  };
  const saveTemplate = async () => {
    try { await api.put(`/studio/quote-terms-template`, { terms: quote?.terms || {} }); setTemplateTerms(quote?.terms || {}); toast.success("Plantilla de condiciones guardada"); }
    catch { toast.error("No se pudo guardar la plantilla"); }
  };
  const useTemplate = () => {
    if (templateTerms && Object.keys(templateTerms).length) { setQuote((q) => ({ ...q, terms: { ...(q.terms || {}), ...templateTerms } })); toast.success("Plantilla aplicada"); }
    else toast.error("Aún no tienes una plantilla guardada");
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/crm/leads/${lead.id}/quote`, { ...quote, folio }); toast.success(`Presupuesto guardado · valor del pipeline ${money(data.value)}`); setCurrentFolio(data.folio || currentFolio); setFolio(""); onSaved(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); return false; }
    finally { setSaving(false); }
  };

  const convert = async (studio) => {
    if (!window.confirm(`¿Convertir este lead en proyecto ${studio.toUpperCase()}? El presupuesto se copiará tal cual a la cotización del proyecto y el lead pasará a "Ganado".`)) return;
    setConverting(true);
    try {
      if (quote && quote.items.length) { const ok = await save(); if (!ok) { setConverting(false); return; } }
      const { data } = await api.post(`/crm/leads/${lead.id}/convert`, { studio });
      toast.success(`Proyecto ${studio.toUpperCase()} creado`); onSaved();
      window.location.href = data.link;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo convertir"); setConverting(false); }
  };

  const c = quote ? computeQuote(quote) : null;

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/crm/leads/${lead.id}/quote/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Presupuesto-${lead.name}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Guarda un presupuesto con partidas antes de exportar"); }
  };
  const sendEmail = async () => {
    if (!email || !email.includes("@")) { toast.error("Escribe un correo válido"); return; }
    try { await api.post(`/crm/leads/${lead.id}/quote/email`, { to: email }); toast.success(`Presupuesto enviado a ${email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar"); }
  };
  const genLink = async () => {
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/approval-link`, { studio: approvalStudio });
      const full = window.location.origin + data.path;
      setApprovalUrl(full);
      try { await navigator.clipboard.writeText(full); toast.success("Enlace de aprobación copiado"); } catch { toast.success("Enlace de aprobación generado"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Guarda un presupuesto con partidas primero"); }
  };
  const openInvoice = async () => {
    if (!quote || !(quote.items || []).some((it) => it.kind !== "header" && Number(it.unit_price) > 0)) { toast.error("Agrega partidas con importe antes de facturar"); return; }
    const ok = await save();  // asegura que las partidas tengan id en la base de datos
    if (ok) setInvoiceOpen(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.35, ease }} onClick={(e) => e.stopPropagation()} data-testid="lead-drawer" className="w-full max-w-2xl bg-cream h-full overflow-y-auto thin-scroll p-8">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="min-w-0">
            <div className="overline text-navy/40">Presupuesto del lead</div>
            <h3 className="text-2xl font-medium truncate">{lead.name}</h3>
            <div className="text-sm text-navy/50">{lead.company} · {lead.segment}</div>
          </div>
          <button onClick={onClose} data-testid="lead-drawer-close" className="text-navy/40 hover:text-navy shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {converted && (
          <div className="mb-5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 text-sm text-emerald-700 flex items-center justify-between gap-3">
            <span>Este lead ya se convirtió en proyecto {converted.studio.toUpperCase()}.</span>
            <a href={`/os/${converted.studio}/${converted.id}`} className="underline shrink-0">Abrir proyecto</a>
          </div>
        )}

        {!quote ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div>
        ) : (
          <>
            {locked && (
              <div className="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-700 flex items-center gap-2" data-testid="lead-quote-locked">
                <Check className="w-4 h-4 shrink-0" /> {lockReason || "Este presupuesto ya no se puede modificar."} Puedes verlo, descargarlo o facturarlo.
              </div>
            )}
            <QuoteBuilder value={quote} onChange={setQuote} readOnly={locked} />

            <fieldset disabled={locked} className="pt-4 mt-4 border-t border-navy/10 space-y-3 min-w-0 p-0 m-0" data-testid="lead-quote-terms">
              <QuoteHeaderFields terms={quote.terms || {}} onTerm={setT} folio={folio} onFolio={setFolio} currentFolio={currentFolio} />
              <div className="pt-3 border-t border-navy/10" />
              <div className="text-sm font-medium text-navy flex items-center gap-1.5"><FileText className="w-4 h-4 text-violet" /> Condiciones de la cotización</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-navy/50 block mb-1">Fecha de cotización</label><input type="date" className={input} value={quote.terms?.fecha || ""} onChange={(e) => setT("fecha", e.target.value)} data-testid="lead-terms-date" /></div>
                <div><label className="text-xs text-navy/50 block mb-1">Condiciones de pago</label><input className={input} placeholder="Ej. 50% anticipo, 50% contra entrega" value={quote.terms?.payment_terms || ""} onChange={(e) => setT("payment_terms", e.target.value)} data-testid="lead-terms-payment" /></div>
              </div>
              <div><label className="text-xs text-navy/50 block mb-1">Cuentas / datos donde se debe pagar</label><textarea rows={2} className={input} placeholder="Banco, titular, CLABE / número de cuenta, referencia…" value={quote.terms?.payment_accounts || ""} onChange={(e) => setT("payment_accounts", e.target.value)} data-testid="lead-terms-accounts" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-navy/50 block mb-1">Garantía</label><input className={input} placeholder="Ej. 12 meses en mano de obra" value={quote.terms?.warranty || ""} onChange={(e) => setT("warranty", e.target.value)} data-testid="lead-terms-warranty" /></div>
                <div><label className="text-xs text-navy/50 block mb-1">Vigencia</label><input className={input} placeholder="Ej. 15 días" value={quote.terms?.validity || ""} onChange={(e) => setT("validity", e.target.value)} data-testid="lead-terms-validity" /></div>
              </div>
              <div><label className="text-xs text-navy/50 block mb-1">Otras condiciones / notas</label><textarea rows={2} className={input} placeholder="Condiciones generales, tiempos de entrega, exclusiones, etc." value={quote.terms?.conditions || ""} onChange={(e) => setT("conditions", e.target.value)} data-testid="lead-terms-notes" /></div>
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-navy/10">
                <div className="text-xs text-navy/50 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-violet" /> Logo en el PDF</div>
                {logoUrl ? <img src={logoUrl} alt="Logo" className="h-10 max-w-[130px] object-contain rounded border border-navy/10 bg-white p-1" data-testid="lead-logo-preview" /> : <span className="text-xs text-navy/40">Sin logo</span>}
                <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="lead-logo-input" onChange={uploadLogo} />
                <button type="button" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="lead-logo-upload" className="text-xs text-violet hover:underline inline-flex items-center gap-1 disabled:opacity-50">{logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} {logoUrl ? "Cambiar logo" : "Subir logo"}</button>
                {logoUrl && <button type="button" onClick={removeLogo} data-testid="lead-logo-remove" className="text-xs text-navy/40 hover:text-destructive inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Quitar</button>}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-navy/50">Color de marca (PDF y enlace)</span>
                  <input type="color" value={accent} onChange={(e) => saveAccent(e.target.value)} data-testid="lead-accent-color" className="w-8 h-8 rounded cursor-pointer border border-navy/10 bg-white p-0.5" title="Color de marca" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button type="button" onClick={saveTemplate} className="text-xs text-violet hover:underline inline-flex items-center gap-1" data-testid="lead-terms-save-template"><Check className="w-3.5 h-3.5" /> Guardar como plantilla</button>
                <button type="button" onClick={useTemplate} className="text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1" data-testid="lead-terms-use-template"><RefreshCw className="w-3.5 h-3.5" /> Usar mi plantilla</button>
              </div>
            </fieldset>

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-navy/10 pt-5">
              {!locked && <button data-testid="lead-quote-save" onClick={save} disabled={saving} className="rounded-full bg-navy text-white px-6 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Guardar presupuesto</button>}
              <button data-testid="lead-quote-pdf" onClick={downloadPdf} className="rounded-full border border-navy/15 px-5 py-3 text-sm hover:bg-navy/5 inline-flex items-center gap-2"><Download className="w-4 h-4" /> PDF</button>
              <button data-testid="lead-quote-invoice" onClick={openInvoice} className="rounded-full bg-gradient-violet text-white px-5 py-3 text-sm hover:opacity-90 inline-flex items-center gap-2"><ReceiptText className="w-4 h-4" /> Facturar presupuesto</button>
            </div>

            <div className="mt-6 pt-5 border-t border-navy/10">
              <QuoteComments path={`/crm/leads/${lead.id}/quote/comments`} role="company" accent={accent} />
            </div>

            <div className="mt-4 rounded-xl border border-navy/10 p-4 space-y-3">
              <div className="text-xs font-semibold text-navy/60 uppercase tracking-wide">Enviar al cliente</div>
              <div className="flex flex-wrap gap-2 items-center">
                <input data-testid="lead-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@cliente.com" className="flex-1 min-w-[200px] bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet" />
                <button data-testid="lead-email-send" onClick={sendEmail} className="rounded-full border border-navy/15 px-4 py-2 text-sm hover:bg-navy/5 inline-flex items-center gap-1.5"><Send className="w-4 h-4" /> Enviar PDF</button>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-violet/20 bg-violet/5 p-4 space-y-3">
              <div className="text-xs font-semibold text-violet uppercase tracking-wide">Aprobación del cliente</div>
              <p className="text-xs text-navy/50">Genera un enlace público para que el cliente revise y apruebe el presupuesto. Al aprobar, el lead se convierte automáticamente en el proyecto que elijas.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <select data-testid="lead-approval-studio" value={approvalStudio} onChange={(e) => setApprovalStudio(e.target.value)} className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
                  <option value="gout">Convertir a GOÛT</option>
                  <option value="tagma">Convertir a TAGMA</option>
                </select>
                <button data-testid="lead-approval-link" onClick={genLink} className="rounded-full bg-gradient-violet text-white px-4 py-2 text-sm hover:opacity-90 inline-flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Generar enlace</button>
              </div>
              {approvalUrl && <input data-testid="lead-approval-url" readOnly value={approvalUrl} onFocus={(e) => e.target.select()} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-xs font-mono text-navy/60 outline-none" />}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-navy/10 pt-5">
              <span className="text-xs text-navy/40">Convertir a proyecto ahora:</span>
              <button data-testid="lead-convert-gout" onClick={() => convert("gout")} disabled={converting} className="rounded-full bg-gradient-violet text-white px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">{converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} GOÛT</button>
              <button data-testid="lead-convert-tagma" onClick={() => convert("tagma")} disabled={converting} className="rounded-full bg-gradient-violet text-white px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">{converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} TAGMA</button>
            </div>
          </>
        )}
      </motion.div>
      {invoiceOpen && quote && (
        <QuoteInvoiceModal source="lead" leadId={lead.id} quote={quote}
          defaultName={lead.company || lead.name} defaultEmail={lead.email}
          onClose={() => setInvoiceOpen(false)}
          onDone={() => { api.get(`/crm/leads/${lead.id}/quote`).then((r) => { const q = r.data || {}; setQuote((prev) => ({ ...prev, items: q.items || prev.items })); }).catch(() => {}); onSaved(); }} />
      )}
    </div>
  );
}

function ClientDetail({ client, onClose }) {
  const c = client;
  const byStudio = c.studios.map((s) => ({ studio: s, items: c.items.filter((it) => it.studio === s) }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()} data-testid="client-detail" className="w-full max-w-lg bg-cream rounded-xl p-8 max-h-[90vh] overflow-y-auto thin-scroll">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-gradient-violet flex items-center justify-center text-white text-sm font-medium shrink-0">{c.client.slice(0, 2).toUpperCase()}</div>
            <div className="min-w-0">
              <div className="overline text-navy/40">Ficha de cliente 360°</div>
              <h3 className="text-xl font-medium truncate">{c.client}</h3>
              {c.email && <a href={`mailto:${c.email}`} className="text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</a>}
            </div>
          </div>
          <button onClick={onClose} className="text-navy/40 hover:text-navy shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 mb-5">
          {c.active
            ? <span className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600">Cliente activo</span>
            : <span className="text-[11px] px-2.5 py-1 rounded-full bg-navy/5 text-navy/40">Inactivo</span>}
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-navy/5 text-navy/50">{c.items.length} proyecto(s)/pedido(s)</span>
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-navy/5 text-navy/50">{c.studios.length} studio(s)</span>
        </div>

        <div className="space-y-4">
          {byStudio.map(({ studio, items }) => {
            const Icon = STUDIO_ICON[studio] || Layers;
            return (
              <div key={studio}>
                <div className="flex items-center gap-2 mb-2 text-violet"><Icon className="w-4 h-4" /><span className="text-sm font-medium text-navy">{studio}</span></div>
                <div className="space-y-1.5">
                  {items.map((it, j) => (
                    <Link key={j} to={it.link} onClick={onClose} data-testid={`client-item-${studio}-${j}`}
                      className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-2.5 hover:border-violet transition-colors group">
                      <span className="flex-1 min-w-0 truncate text-sm">{it.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${it.active ? "bg-emerald-500/10 text-emerald-600" : "bg-navy/5 text-navy/40"}`}>{it.status}</span>
                      <ArrowRight className="w-4 h-4 text-navy/25 group-hover:text-violet shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
